import React, { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { queryClient } from '../../../../config/queryClient';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { StickyFooter } from '../../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { queryKeys } from './api/queryKeys';
import { useAssetBucketMutation } from './api/useAssetBucketMutation';
import { AssetBucketingDTO } from './api/useAssetBucketQuery';
import { EMPTY_BUCKET, FormFieldValues, validation } from './AssetBucketing.meta';
import { BucketList } from './components/BucketList/BucketList';
import { Header } from './components/Header/Header';
import { PageContentForm } from './components/PageContentForm/PageContentForm';
import { PageLayoutForm } from './components/PageLayoutForm/PageLayoutForm';
import { useAssetBucketing } from './useAssetBucketing';
import { useAdvisorAssetBucketMutation } from './api/useAdvisorAssetBucketMutation';
import { selectHasAdvisorAssetBucketingLayoutAccess } from '../../../CommonSelectors/CommonAreaSelector';
import { updateAssetBucketingHeadline } from '../../../AdviserArea/AdviserAreaActions';
import { showOPLConfirmationDialog } from '../../../helpers/showOPLConfirmationDialog';

export const AssetBucketing = () => {
  const { data: assetBuckets, isLoading: isRequestingAssetBuckets } = useAssetBucketing();

  if (isRequestingAssetBuckets) {
    return (
      <Box>
        <Header />
        <FullPageLoading text="Loading" />
      </Box>
    );
  }

  if (!assetBuckets) {
    return null;
  }

  return <AssetBucketingContent assetBuckets={assetBuckets} />;
};

interface Props {
  assetBuckets: AssetBucketingDTO;
}

const AssetBucketingContent: React.FC<Props> = ({ assetBuckets }) => {
  const dispatch = useDispatch();

  const hasAdvisorAssetBucketingLayoutAccess = useSelector(
    selectHasAdvisorAssetBucketingLayoutAccess,
  );

  const { buckets, isDefaultAssetBucketingLayout, ...rest } = assetBuckets;

  const methods = useForm<FormFieldValues>({
    defaultValues: {
      buckets: buckets || [EMPTY_BUCKET, EMPTY_BUCKET],
      ...rest,
    },
    resolver: yupResolver(validation),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const { fields, append, remove, replace, update } = useFieldArray({
    shouldUnregister: true,
    control,
    name: 'buckets',
  });

  const { mutateAsync: saveAssetBucket, isLoading: savingAssetBucket } = useAssetBucketMutation();

  const {
    mutateAsync: saveAdvisorAssetBucket,
    isLoading: savingAdvisorAssetBucket,
  } = useAdvisorAssetBucketMutation();

  useEffect(() => {
    if (isEmpty(methods.formState.dirtyFields)) {
      ADV.util.Router.unregisterNavigateAwayCallback();
    } else {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    }
  }, [methods.formState]);

  const handlePreviewClick = async () => {
    methods.clearErrors();

    if (await methods.trigger(undefined, { shouldFocus: true })) {
      queryClient.setQueryData(queryKeys.assetBucket(), {
        ...methods.getValues(),
        isDefaultAssetBucketingLayout: isDefaultAssetBucketingLayout
          ? !isDirty
          : isDefaultAssetBucketingLayout,
      });

      ADV.util.Router.go2('AssetBucketingPreview');
    }
  };

  return (
    <Box>
      <Header resetForm={methods.reset} />

      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(async formData => {
            try {
              const { reset } = methods;

              const data = {
                ...formData,

                isDefaultAssetBucketingLayout: isDefaultAssetBucketingLayout
                  ? !isDirty
                  : isDefaultAssetBucketingLayout,
              };

              const defaultAssetBuckets = hasAdvisorAssetBucketingLayoutAccess
                ? await saveAdvisorAssetBucket(data)
                : await saveAssetBucket(data);

              reset(defaultAssetBuckets);

              dispatch(updateAssetBucketingHeadline(formData.headline));

              toast.success(
                formData.enableManagementStylesBucketing
                  ? 'Orion Management Style buckets were successfully saved'
                  : 'Asset buckets were successfully saved',
              );
            } catch (err) {
              if (err instanceof Error) {
                toast.error(err.message);
              }
            }
          })}
        >
          <PageLayoutForm appendBucket={append} removeBucket={remove} />

          <PageContentForm />

          <BucketList
            bucketFields={fields}
            updateBucket={update}
            replaceBucket={replace}
            isDefaultAssetBucketingLayout={isDefaultAssetBucketingLayout}
          />

          <StickyFooter fullWidthChildren>
            {/* Hide bucket shadow */}
            <Box
              bg="bg"
              css="
                bottom: 0;
                left: -20px;
                position: absolute;
                top: 0;
                width: 105%;
              "
            />
            <Box display="flex" flex={1} justifyContent="space-between">
              <Button type="button" variant="secondary" size="large" onClick={handlePreviewClick}>
                Preview
              </Button>

              <Button
                type="submit"
                size="large"
                isLoading={savingAssetBucket || savingAdvisorAssetBucket}
                disabled={savingAssetBucket || savingAdvisorAssetBucket}
              >
                Save
              </Button>
            </Box>
          </StickyFooter>
        </form>
      </FormProvider>
    </Box>
  );
};
